<script>
  import Page from '@components/page.svelte'
  import BoxedText from '@components/general/boxed-text.svelte'
  import Table from '@components/table'
  import FilterBar from '@components/table/filter-bar.svelte'
  import Dropdown from '@components/general/dropdown.svelte'
  import Button from '@components/buttons/button.svelte'
  import MemberForm from '@components/members/form.svelte'
  import ConfirmDeleteModal from '@components/general/confirm-delete-modal.svelte'
  import { usePageParam } from '@hooks/use-page-param.svelte'
  import { useSelection } from '@hooks/use-selection.svelte'
  import { CirclePlus, EllipsisVertical, Pencil, Send, Trash2, Wand, CheckCheck, CircleX, Users, UserCheck, UserX, UserMinus, Clock, CircleAlert, CircleCheck } from 'lucide-svelte'
  import { sortItems } from '@lib/utils'
  import { router } from '@inertiajs/svelte'
  import { formatDateTime } from '@lib/utils/date';

  let { members = [], pagination, ids = [], programmes = [] } = $props()

  let selection = $derived(useSelection({ initialItems: members, allIds: ids }))
  let sort = usePageParam({ key: "sort", defaultValue: "newest", items: sortItems })
  let formOpen = $state(false)
  let deleteModalOpen = $state(false)
  let memberToDelete = $state(null)
  let deleteProcessing = $state(false)

  let memberHasActiveMembership = $derived(
    memberToDelete?.memberships?.some(m => m.status === 'active') ?? false
  )

  function openDeleteModal(member) {
    memberToDelete = member
    deleteModalOpen = true
  }

  function handleDelete() {
    if (!memberToDelete) return
    deleteProcessing = true
    router.delete(`/admin/members/${memberToDelete.id}`, {
      onFinish: () => {
        deleteProcessing = false
        deleteModalOpen = false
        memberToDelete = null
      }
    })
  }

  function batchDelete() {
    if (!window.confirm(`Delete ${selection.count} selected member(s)? This cannot be undone. Any associated memberships will also be deleted.`)) return
    router.delete('/admin/members/batch_delete', { data: { ids: selection.ids }, onSuccess: selection.clear() })
  }

  function resendWelcomeEmail(member) {
    router.post(`/admin/members/${member.id}/resend_password`)
  }

  let confirmationStatus = usePageParam({ key: "confirmation_status", defaultValue: "", items: [
    { value: "", label: "Confirmation: All", icon: CheckCheck },
    { value: "confirmed", label: "Confirmed", icon: CircleCheck },
    { value: "unconfirmed", label: "Unconfirmed", icon: CircleX },
  ] })

  let membershipStatus = usePageParam({ key: "membership_status", defaultValue: "", items: [
    { value: "", label: "Membership: All", icon: Users },
    { value: "active", label: "Membership: Active", icon: UserCheck },
    { value: "inactive", label: "Membership: Pending", icon: Clock },
    { value: "none", label: "Membership: None", icon: UserX },
  ] })
</script>
<Page title="Members" subtitle="Manage your members" links={[{ text: "Admin", href: "/admin" }]}>

  {#snippet buttons()}
    <Button text="New member" onclick={() => formOpen = true} Icon={CirclePlus} />
  {/snippet}

  <FilterBar {pagination} paginationProps={{ summary: true, terms: ["member", "members"] }}>
    {#snippet filters()}

      <!-- Sort -->
      <Dropdown buttonProps={{ text: sort.label || "Sort", size: "md", Icon: sort.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each sort.items as sortItem (sortItem.value)}
            {@render item({ text: sortItem.label, icon: sortItem.icon, onSelect: () => sort.setValue(sortItem.value) })}
          {/each}
        {/snippet}
      </Dropdown>

      <!-- Confirmation status filter -->
      <Dropdown buttonProps={{ text: confirmationStatus.label || "Confirmation", size: "md", Icon: confirmationStatus.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each confirmationStatus.items as statusItem (statusItem.value)}
            {@render item({ text: statusItem.label, icon: statusItem.icon, onSelect: () => confirmationStatus.setValue(statusItem.value) })}
          {/each}
        {/snippet}
      </Dropdown>

      <!-- Membership status filter -->
      <Dropdown buttonProps={{ text: membershipStatus.label || "Membership", size: "md", Icon: membershipStatus.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each membershipStatus.items as statusItem (statusItem.value)}
            {@render item({ text: statusItem.label, icon: statusItem.icon, onSelect: () => membershipStatus.setValue(statusItem.value) })}
          {/each}
        {/snippet}
    </Dropdown>

      <!-- Batch actions -->
      {#if selection?.populated}
        <Dropdown buttonProps={{ text: `${selection.count} selected`, size: "md", Icon: Wand }} width="16rem" align="start" itemSize="sm">
          {#snippet contents(item)}
            {@render item({ text: "Delete members", icon: Trash2, onSelect: batchDelete })}
          {/snippet}
        </Dropdown>
      {/if}

    {/snippet}
  </FilterBar>

  <Table.Root empty={members.length === 0} emptyMessage="No members found">
    <Table.Head>
      <Table.SelectionDropdownCell {selection} />
      <Table.Header width="25%">Name</Table.Header>
      <Table.Header width="25%">Email</Table.Header>
      <Table.Header width="25%">Created</Table.Header>
      <Table.Header width="15%" align="center">Membership</Table.Header>
      <Table.Header width="10%" align="center">Actions</Table.Header>
    </Table.Head>
    <Table.Body>
      {#each members as member (member.id)}
        {@const membership_status = member.memberships?.some(m => m.status === "active") ? "active" : member.memberships?.some(m => m.status === "inactive") ? "inactive" : member.memberships?.some(m => m.status === "canceled") ? "canceled" : "none"}
        {@const membershipVariant = { active: "success", inactive: "warning", canceled: "error", none: "default" }}
        {@const membershipLabel = { active: "Active", inactive: "Pending", canceled: "Cancelled", none: "None" }}
        <Table.Row>

          <Table.Cell width="3rem" align="center">
            <input type="checkbox" checked={selection.has(member.id)} onclick={e => selection.toggle(member.id, e)} />
          </Table.Cell>

          <Table.Cell width="25%">
            <Button variant="link" path={`/admin/members/${member.id}`} text={member.name} />
          </Table.Cell>

          <Table.Cell width="25%" containerClass="flex justify-start items-center">
            {member.email_address}
            {#if member.confirmed}
              <Button variant="naked" Icon={CircleCheck} size="sm" hint="Email confirmed" iconProps={{ class: "text-green-500" }} />
            {:else}
              <Button variant="naked" Icon={CircleAlert} size="sm" hint="Email not confirmed" iconProps={{ class: "text-gray-400" }} />
            {/if}
          </Table.Cell>

          <Table.Cell width="25%">
            {formatDateTime(member.created_at)}
          </Table.Cell>

          <Table.Cell width="15%" align="center">
            <BoxedText variant={membershipVariant[membership_status]} size="xs" text={membershipLabel[membership_status]} />
          </Table.Cell>

          <Table.Cell width="10%" align="center">
            <Dropdown buttonProps={{ variant: "naked", Icon: EllipsisVertical }} showTriggerButton={false} width="16rem">
              {#snippet contents(item)}
                {@render item({ text: "Edit", icon: Pencil, disabled: true })}
                {@render item({ text: "Delete", icon: Trash2, onSelect: () => openDeleteModal(member) })}
                {@render item({ text: "Resend welcome email", icon: Send, onSelect: () => resendWelcomeEmail(member) })}
                {#if !member.confirmed}
                  {@render item({ text: "Resend confirmation", icon: Send, disabled: true })}
                {/if}
              {/snippet}
            </Dropdown>
          </Table.Cell>
        </Table.Row>
      {/each}
    </Table.Body>
  </Table.Root>

  <Table.Pagination {pagination} paginationProps={{ summary: true, terms: ["member", "members"] }} />

</Page>

<MemberForm bind:open={formOpen} {programmes} />

<ConfirmDeleteModal
  bind:open={deleteModalOpen}
  title="Delete member"
  message={memberHasActiveMembership
    ? `${memberToDelete?.name} has an active membership. Deleting this account will cancel their membership, remove all associated data, and revoke their access immediately.`
    : `This will permanently delete ${memberToDelete?.name}'s account and all associated data.`
  }
  warningMessage="This action cannot be undone."
  confirmValue={memberHasActiveMembership ? memberToDelete?.email_address : null}
  confirmLabel="Type"
  buttonText="Delete member"
  processing={deleteProcessing}
  onConfirm={handleDelete}
  onClose={() => memberToDelete = null}
/>
